import { useContext, useEffect, useRef } from 'react'
import { useFormikContext } from 'formik'
import { StoreContext } from '~/store'
import { showErrorModal } from '~/utils'
import { fetchEta, fetchPriceEstimate, fetchServiceAvailability } from '~/lib/api'
import { getAvailableVehicleOptions, normalizePriceType, getPaymentMethodKey, normalizeAvailability } from '~/utils/data'
import { getDefaultAvailability } from './service-availability-responses'
import i18next from '~/i18n'

const getNextTimeToRun = estimate => {
  const validUntil = estimate?.validUntil
  if (!validUntil) return false
  // Refresh 30 seconds before the token expires; discard if already past
  const ms = new Date(validUntil).getTime() - Date.now() - 30000
  return ms > 0 ? ms : false
}


/**
 * Component that fetches data depending on the Formik 
 * values for example service availability and price estimates.
 */
export default function Fetcher() {
  const estimateTimeout = useRef()
  const hasDefaultedPayment = useRef()
  const estimatePriceRef = useRef(null)

  const { values, setFieldValue, setFieldError } = useFormikContext()
  const { user, preferredPaymentAccount, preferredPaymentAccountCompany, preferredPaymentMethod, preferredPaymentMethodCompany, currentPaymentAccount, currentPaymentAccountCompany, currentPaymentMethod, currentPaymentMethodCompany, isCompanyTrip } = useContext(StoreContext)
  
  const getPaymentType = account => {
    console.log('getPaymentType:', account)
    if (account?.type === 'creditcard') {
      return `CreditCard-${account.accountId}`
    }
    else if (account?.type === 'TSAB' || account?.type === 'TCA') {
      return `Account-${account.accountNumber}`
    }
    return 'DefaultNoSelect'
  }

  const estimatePrice = async (freshAvailability = null) => {
    console.log('estimatePrice')
    // Filter out vouchers that the current payment method doesn't allow,
    // guarding against stale closure timing between payment-change and voucher-clear effects.
    const selectedKey = getPaymentMethodKey(values.payment, values.account)
    const vouchersDisabled = selectedKey && values.priceType?.paymentMethodConfigurations
      ? !values.priceType.paymentMethodConfigurations.find(c => c.paymentMethod === selectedKey)?.voucherEnabled
      : !values.priceType?.voucherEnabled
    const valuesForEstimate = (vouchersDisabled && values.vouchers?.length > 0)
      ? { ...values, vouchers: [] }
      : values
    setFieldValue('priceIsLoading', true, false)
    try {
      const priceEstimate = await fetchPriceEstimate(valuesForEstimate, freshAvailability)
      console.log('Estimate:', priceEstimate)
      setFieldValue('priceEstimate', priceEstimate, false)
      setFieldValue('priceToken', priceEstimate.priceToken, false)
      const ms = getNextTimeToRun(priceEstimate)
      if (ms) {
        console.log(`Next run in ${ms} milliseconds`)
        clearTimeout(estimateTimeout.current)
        estimateTimeout.current = setTimeout(() => estimatePriceRef.current?.(), ms)
      }
    }
    catch(error) {
      if (error.name === 'AbortError') {
        return
      }
      else if (error?.response?.error === 'FIXED_PRICE_FOR_DESTINATION_NOT_FOUND' 
        || error?.response?.error === 'FIXED_PRICE_FOR_ORIGIN_NOT_FOUND') {
        console.error(error.response)
        const message = i18next.language === 'sv' ? 'Tyvärr ej tillgängligt' : 'Not available'
        setFieldError('fixedPrice', message) // TODO: This error should be fetched from the JSON
        setFieldValue('fixedPrice', false, false)
      }
      else {
        console.error(error.response)
        if (error.status === 500) {
          showErrorModal(i18next.t('error-no-estimate'))
        }
        setFieldError('fixedPrice', error?.response?.message)
        setFieldValue('fixedPrice', false, false)
      }
    }
    setFieldValue('priceIsLoading', false, false)
  }
  // Keep ref current so the scheduled timeout always invokes the latest closure
  estimatePriceRef.current = estimatePrice

  // Get priceTypes for current context:
  // - if a provider is selected (multiple-partner mode), use that provider's priceTypes
  // - otherwise fall back to the flat/aggregated priceTypes
  const getPriceTypes = (availability) => {
    const providers = availability?.servicesByProvider
    if (Array.isArray(providers) && values.selectedProviderId) {
      const selected = providers.find(p => p.providerData.providerId === values.selectedProviderId)
      if (selected) return (selected.priceAndPaymentRules?.priceTypes || []).map(normalizePriceType)
    }
    return (normalizeAvailability(availability)?.priceAndPaymentRules?.priceTypes || []).map(normalizePriceType)
  }

  const getAutoValues = (availability, estimate = null) => {
    // Check fixed price / taximeter availability
    console.log('getAutoValues -- ', availability, estimate)
    const normalized = normalizeAvailability(availability)
    let fixedPrice = null
    const priceTypes = (normalized.priceAndPaymentRules?.priceTypes || []).map(normalizePriceType)
    const fixedPriceAvailable = priceTypes.find(item => item.type === 'FIXED') !== undefined
    const taxiMeterAvailable = priceTypes.find(item => item.type === 'TAXIMETER') !== undefined
    const priceAvailable = serviceType => {
      return estimate.prices?.find(item => item.serviceType === serviceType) !== undefined
    }

    if (fixedPriceAvailable && !taxiMeterAvailable) fixedPrice = true
    else if (!fixedPriceAvailable && taxiMeterAvailable) fixedPrice = false

    if (normalized?.availabilities?.length > 0) {
      const st = values.serviceType === 'estate' ? 'taxi' : values.serviceType;
      const a = normalized.availabilities.find(item => item.serviceType === st)
      console.log("Matching availability:", a)
      // Check if price is available
      if (a !== undefined && a.availableAt === 'AS_REQUESTED' && estimate) {
        if (fixedPriceAvailable && !priceAvailable(a.serviceType)) fixedPrice = false
        return { pickupTime: null, serviceType: null, fixedPrice }
      }
      // Set pickupTime autoValue to time for matching service type
      if (a !== undefined && a.availableAt === 'DELAYED') {
        if (estimate && fixedPriceAvailable && !priceAvailable(a.serviceType)) fixedPrice = false
        return { pickupTime: a.pickupTime, serviceType: null, fixedPrice }
      }
      // If the selected service type is NOT_AVAILABLE, select the first available
      if (a === undefined || a.availableAt === 'NOT_AVAILABLE') {
        const b = normalized.availabilities.find(item => item.availableAt === 'AS_REQUESTED' || item.availableAt === 'DELAYED')
        console.log("First available availability:", b)
        if (b !== undefined) {
          if (estimate && fixedPriceAvailable && !priceAvailable(b.serviceType)) fixedPrice = false
          return { pickupTime: b.pickupTime, serviceType: b.serviceType, fixedPrice }
        }
      }
    }
    return { pickupTime: null, serviceType: null, fixedPrice, fixedPriceAvailable}
  }

  // const fetchAvailability = () => {
  //   const key = values.pickupTime === '' ? 'availability.direct' : 'availability.prebook'
  //   setFieldValue('availabilityIsLoading', true, false)
  //   fetchServiceAvailability(values, user)
  //     .then((response) => {
  //       console.log(key, response)

  //       // Sort and filter the availabilities
  //       if (response?.availabilities?.length > 0) {
  //         const order = ['taxi', 'largeCar', 'business']
  //         response.availabilities = response.availabilities.filter(item => order.includes(item.serviceType))
  //         response.availabilities.sort((a, b) => {
  //           const indexA = order.indexOf(a.serviceType)
  //           const indexB = order.indexOf(b.serviceType)    
  //           if (indexA === -1 && indexB === -1) return 0
  //           else if (indexA === -1 && indexB > -1) return 1
  //           else if (indexA > -1 && indexB === -1) return -1
  //           return indexA - indexB
  //         })
  //       }

  //       setFieldValue(key, response, false)
  //       setFieldValue('availabilityIsLoading', false, false)
  //     })
  //     .catch((error) => {
  //       // NOTE: Any error but AbortError results in using the default values
  //       if (error.name !== 'AbortError') {
  //         console.error(error)
  //         // if (error.status === 500) {
  //           // Return default values depending on region
  //           console.log('We got a status 500 so using default values')
  //           const defaultAvailability = getDefaultAvailability(values.departure.address.region)
  //           console.log(defaultAvailability)
  //           setFieldValue(key, defaultAvailability, false)
  //           setFieldValue('availabilityIsLoading', false, false)
  //         // }
  //         // else {
  //         //   setFieldValue(key, false, false)
  //         //   setFieldValue('availabilityIsLoading', false, false)
  //         // }
  //       }
  //     })
  // }


  // NOTE: This is a combined version of fetch service availability and fetch estimate
  const fetchData = async () => {
    // First we check service availability
    let availability = false
    const key = values.pickupTime === '' ? 'availability.direct' : 'availability.prebook'
    setFieldValue('availabilityIsLoading', true, false)

    // Pre-set priceIsLoading so the spinner shows during the availability fetch too,
    // not just after it completes. Only when we know price will be needed.
    const currentFixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice
    if (currentFixedPrice && values.departure?.address && values.destination?.address && isNaN(values.bookingId)) {
      setFieldValue('priceIsLoading', true, false)
    }

    try {
      availability = await fetchServiceAvailability(values, user)

      // TODO: Needs to handle delivery too
      // Sort and filter the availabilities (works on flat or v4 normalized view)
      const order = values.serviceType === 'delivery' ? ['delivery'] : ['taxi', 'largeCar', 'business']
      const sortAvailabilities = (avails) => {
        return avails
          .filter(item => order.includes(item.serviceType))
          .sort((a, b) => {
            const indexA = order.indexOf(a.serviceType)
            const indexB = order.indexOf(b.serviceType)
            if (indexA === -1 && indexB === -1) return 0
            else if (indexA === -1 && indexB > -1) return 1
            else if (indexA > -1 && indexB === -1) return -1
            return indexA - indexB
          })
      }
      if (availability?.servicesByProvider) {
        // v4 multi-provider: sort each provider's availabilities
        availability.servicesByProvider = availability.servicesByProvider.map(p => ({
          ...p,
          availabilities: sortAvailabilities(p.availabilities || [])
        }))
      } else if (availability?.availabilities?.length > 0) {
        availability.availabilities = sortAvailabilities(availability.availabilities)
      }
    }
    catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error)
        // Return default values depending on region
        console.log('We got a status 500 so using default values')
        availability = getDefaultAvailability(values.departure.address.region)
      }
      else {
        return // NOTE: If we get an AbortError we return early
      }
    }

    // Get auto values
    const autoValues = getAutoValues(availability)
    
    // Then we try to fetch a price estimate
    const fixedPrice = autoValues?.fixedPrice !== null ? autoValues?.fixedPrice : values.fixedPrice
    if (fixedPrice && values.departure?.address && values.destination?.address && isNaN(values.bookingId)) {
      await estimatePrice(availability)
    } else {
      setFieldValue('priceIsLoading', false, false)
    }

    setFieldValue(key, normalizeAvailability(availability), false)
    setFieldValue('availabilityIsLoading', false, false)
  }



  // const getPriceType = () => {
  //   const key = values.pickupTime === '' ? 'direct' : 'prebook'
  //   if (values.availability[key] !== false) {
  //     const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice
  //     const type = fixedPrice ? 'FIXED' : 'TAXIMETER'
  //     return values.availability[key]?.priceAndPaymentRules?.priceTypes?.find(item => item.type === type)
  //   }
  // }

  // if (values.serviceType !== 'delivery') {
    useEffect(() => {
      // NOTE: When fetching service availability we set auto values depending on what's available for selected service type etc.
      // These are used to override user selected values instead of overwriting them. For example:
      // - The user has selected to do a direct booking of a taxi but the service returns a 45 min delay
      // - We then set the pickupTime auto value to delayed pickup time and use that to show info in the booking summary
      // - If the user navigates back and selects an address that does not have a delay the auto value is removed and the pickup time
      //   is shown in the booking summary as normal (ie. direct)

      // In edit mode, check if time has changed
      const inEditMode = !isNaN(values.bookingId) && values.bookingId > 0
      const timeHasChanged = values.originalPickupTime && values.pickupTime &&
        new Date(values.originalPickupTime).getTime() !== new Date(values.pickupTime).getTime()

      const key = values.pickupTime === '' ? 'direct' : 'prebook'
      if (values.availability[key] !== false) {
        let autoValues = getAutoValues(values.availability[key], values.priceEstimate)
        if (autoValues !== undefined) {
          if (values.destination?.address === undefined) {
            console.log('No destination. Disable fixed price')
            autoValues = { ...autoValues, fixedPrice: false }
          }

          // In edit mode with unchanged time, don't set delayed pickupTime autoValue
          // The original booking time is valid even if it would be delayed now
          if (inEditMode && !timeHasChanged && autoValues.pickupTime !== null) {
            console.log('[Fetcher] Skipping pickupTime autoValue - edit mode with unchanged time')
            autoValues = { ...autoValues, pickupTime: null }
          }

          console.log('We got autoValues:', autoValues)
          setFieldValue('autoValues', autoValues, false)
        }
      }
    }, [values.availability, values.serviceType, values.priceEstimate])

    useEffect(() => {
      const key = values.pickupTime === '' ? 'direct' : 'prebook'
      if (values.availability[key] !== false) {
        const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice
        const type = fixedPrice ? 'FIXED' : 'TAXIMETER'
        const priceType = getPriceTypes(values.availability[key]).find(item => item.type === type)

        if (priceType !== undefined) {
          console.log(':: We have a priceType:', priceType)
          setFieldValue('priceType', priceType, false)

          // Check if PayInCar is selected and available
          if (values.payment === 'PayInCar' && priceType.paymentMethods.includes('PayInCar')) {
            // PayInCar is valid, keep it
            hasDefaultedPayment.current = false;
          }
          else if (values.payment === 'PayInCar') {
            // PayInCar was selected but is no longer available for this route/provider — reset
            console.log(':: PayInCar selected but no longer available, resetting to DefaultNoSelect')
            hasDefaultedPayment.current = true;
            setFieldValue('payment', 'DefaultNoSelect', false)
            setFieldValue('account', false, false)
          }
          else if (values?.account && values?.account !== false /*&& values.payment !== 'PayInCar'*/) {
            let isAvailable = false
            if ((values.account?.type === 'creditcard' && priceType.paymentMethods.includes('CreditCard'))
              || (values.account?.type === 'TCA' && priceType.paymentMethods.includes('TravelClearingAccount'))
              || (values.account?.type === 'TSAB' && priceType.paymentMethods.includes('TSABAccount'))) {
              isAvailable = true
            }
            if(values?.account?.restrictedToCompanyId && values.account?.restrictedToCompanyId !== values?.company?.id) isAvailable = false;
            if (!isAvailable) {
              console.log(':: Payment method not available, defaulting to DefaultNoSelect')
              hasDefaultedPayment.current = true;
              setFieldValue('payment', 'DefaultNoSelect', false)
            } else if(hasDefaultedPayment.current) {
              const preferredPayment = values.isCompanyTrip ? (currentPaymentAccountCompany || preferredPaymentAccountCompany) : (currentPaymentAccount || preferredPaymentAccount);
              const preferredMethod = values.isCompanyTrip ? (currentPaymentMethodCompany || preferredPaymentMethodCompany) : (currentPaymentMethod || preferredPaymentMethod);
              console.log(values.isCompanyTrip, ':: preferredPayment:', preferredPayment, 'preferredMethod:', preferredMethod);
              
              // Restore PayInCar if preferred
              if (preferredMethod === 'PayInCar' && priceType.paymentMethods.includes('PayInCar')) {
                console.log(':: Restoring preferred PayInCar')
                hasDefaultedPayment.current = false;
                setFieldValue('payment', 'PayInCar', false)
                setFieldValue('account', false, false)
              } else if (preferredPayment && preferredPayment !== false) {
                console.log(':: setting the default payment:', preferredPayment)
                hasDefaultedPayment.current = false;
                setFieldValue('payment', getPaymentType(preferredPayment), false)
                setFieldValue('account', preferredPayment, false)
              }
            }
          }
        }
        else {
          console.log('Could not find any price type')
          setFieldValue('priceType', false, false)
        }
      }
    }, [values.availability, values.fixedPrice, values.autoValues.fixedPrice, values.selectedProviderId])


    useEffect(() => {
      const key = values.pickupTime === '' ? 'direct' : 'prebook'
      if (values.availability[key] !== false) {
        const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice
        const type = fixedPrice ? 'FIXED' : 'TAXIMETER'
        const priceType = getPriceTypes(values.availability[key]).find(item => item.type === type)

        if (priceType !== undefined) {
          // Get the appropriate payment based on isCompanyTrip
          const preferredPayment = values.isCompanyTrip ? (currentPaymentAccountCompany || preferredPaymentAccountCompany) : (currentPaymentAccount || preferredPaymentAccount);
          const preferredMethod = values.isCompanyTrip ? (currentPaymentMethodCompany || preferredPaymentMethodCompany) : (currentPaymentMethod || preferredPaymentMethod);
          console.log(values.isCompanyTrip, ':: preferredPayment on switch:', preferredPayment, 'preferredMethod:', preferredMethod);

          // Check if PayInCar is preferred and available
          if (preferredMethod === 'PayInCar' && priceType.paymentMethods.includes('PayInCar')) {
            console.log(':: Restoring preferred PayInCar')
            hasDefaultedPayment.current = false;
            setFieldValue('payment', 'PayInCar', false)
            setFieldValue('account', false, false)
          }
          // If there's a preferred payment account, check if it's available
          else if (preferredPayment && preferredPayment !== false) {
            let isAvailable = false
            if ((preferredPayment?.type === 'creditcard' && priceType.paymentMethods.includes('CreditCard'))
              || (preferredPayment?.type === 'TCA' && priceType.paymentMethods.includes('TravelClearingAccount'))
              || (preferredPayment?.type === 'TSAB' && priceType.paymentMethods.includes('TSABAccount'))) {
              isAvailable = true
            }
            if(preferredPayment?.restrictedToCompanyId && preferredPayment.restrictedToCompanyId !== values?.company?.id) isAvailable = false;

            if (isAvailable) {
              console.log(':: Restoring preferred payment:', preferredPayment)
              hasDefaultedPayment.current = false;
              setFieldValue('payment', getPaymentType(preferredPayment), false)
              setFieldValue('account', preferredPayment, false)
            } else {
              console.log(':: Preferred payment not available, defaulting to DefaultNoSelect')
              setFieldValue('payment', 'DefaultNoSelect', false)
              setFieldValue('account', false, false)
            }
          } else {
            console.log(':: No preferred payment, defaulting to DefaultNoSelect')
            setFieldValue('payment', 'DefaultNoSelect', false)
            setFieldValue('account', false, false)
          }
        }
        else {
          console.log('Could not find any price type')
          setFieldValue('priceType', false, false)
        }
      }
    }, [values.isCompanyTrip])
  // }

  useEffect(() => {
    const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice
    if (fixedPrice && values.departure?.address && values.destination?.address) {
      if (isNaN(values.bookingId)) {
        estimatePrice()
      }
    }
    else {
      clearTimeout(estimateTimeout.current)
      if (values.priceEstimate) {
        setFieldValue('priceEstimate', '', false)
        setFieldValue('priceToken', '', false)
      }
    }
    return () => {
      clearTimeout(estimateTimeout.current)
    }
  }, [values.fixedPrice, values.vouchers, values.company])

  useEffect(() => {
    // if (values.serviceType !== 'delivery') {
      if (values.departure?.address) {
        console.log('Fetch availability')
        // fetchAvailability()
        fetchData()
      }
      else {
        console.log('No departure, clear availability and estimate')
        setFieldValue('availability', { direct: false, prebook: false }, false)
        setFieldValue('autoValues', { pickupTime: null, serviceType: null, fixedPrice: null }, false)
        if (values.priceEstimate) {
          setFieldValue('priceEstimate', '', false)
          setFieldValue('priceToken', '', false)
        }
      }
    // }
  }, [values.departure, values.destination, values.intermediateStopLocations, values.pickupTime, values.account?.accountNumber, user?.phone, values.rearFacingChildSeat])


  useEffect(() => {
    if (!isNaN(values.bookingId)) {
      clearTimeout(estimateTimeout.current)
    }
  }, [values.bookingId])

  // Clear selected add-ons that are no longer available for the current service type / provider.
  // Runs whenever serviceType, provider, or availability changes (but not in edit mode).
  useEffect(() => {
    const inEditMode = !isNaN(values.bookingId) && values.bookingId > 0
    if (inEditMode) return

    const available = getAvailableVehicleOptions(values)

    if (values.pet && !available.includes('pet')) {
      setFieldValue('pet', false, false)
    }
    if (values.booster > 0 && !available.includes('boosterSeat1')) {
      setFieldValue('booster', 0, false)
    } else if (values.booster >= 2 && !available.includes('boosterSeat2')) {
      setFieldValue('booster', 1, false)
    }
    if (values.rearFacingChildSeat && !available.includes('rearFacingChildSeat')) {
      setFieldValue('rearFacingChildSeat', false, false)
    }
    if (values.electric && !available.includes('electric')) {
      setFieldValue('electric', false, false)
    }
  }, [values.serviceType, values.autoValues.serviceType, values.selectedProviderId, values.availability])

  return null
}
